/**
 * GradientBuilderPanel - the interactive Gradient sub-editor (WL-012 tranche 2,
 * deck `gradient-builder`).
 *
 * Tranche 1 shipped the Gradient tab as a STUB hint in SegmentFillPanel. This is
 * the real interactive builder that folds the app's existing gradient primitives
 * (`components/gradientBuilder/gradientCss.js`) into the shared SegmentStyling
 * side-pane surface: type selector (Linear / Radial / Conic), a live stop track
 * (add / remove / recolor / reposition), an angle control for linear + conic,
 * and a canonical live preview built from the SAME `buildGradientCss` the
 * renderer uses - so what you see is exactly what a Segment paints.
 *
 * Controlled + presentational: owns no server state. Parent passes `gradient`
 * (a gradientCss `state` object) and `onChange(nextGradient)`; the Redux-wiring
 * container maps that onto `updateSegmentSetting` via `withHistory`.
 *
 * REUSE, NOT DUPLICATION: every gradient string, clamp, and default comes from
 * gradientCss.js (the secured, tested builder) - this file adds only the UI.
 * For a CONIC segment fill it previews through `buildSegmentConicCss` so the
 * seam-blend loop + centroid rule (decisions-log row 36) are honoured here too.
 *
 * WL-001 tokens, SIDE-PANE LAW (row 29), WCAG-AA (ARIA, 44px targets), light theme.
 */

import React, { useMemo } from "react";
import { tokens } from "../../../../theme/tokens";
import {
  GRADIENT_TYPES,
  MIN_GRADIENT_STOPS,
  MAX_GRADIENT_STOPS,
  buildGradientCss,
  defaultGradientState,
  clampAngle,
  clampPosition,
} from "../../../gradientBuilder/gradientCss";
import { buildSegmentConicCss } from "./logic/segmentFill";

const PL = "#9B5AF8";
const INK = "#123051";
const SUB = "#565556";
const LINE = "#e1e1e1";
const PAPER = "#FFFFFF";

const TYPE_LABEL = { linear: "Linear", radial: "Radial", conic: "Conic" };

const S = {
  panel: {
    fontFamily: tokens.fontFamily,
    color: INK,
    background: PAPER,
    border: `1px solid ${LINE}`,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.lg,
    maxWidth: 360,
  },
  h: { margin: 0, fontSize: 13, fontWeight: tokens.fontWeight.bold },
  sub: { margin: `${tokens.spacing.xs}px 0 ${tokens.spacing.md}px`, fontSize: 12, color: SUB },
  row: { display: "flex", gap: tokens.spacing.xs, flexWrap: "wrap", marginBottom: tokens.spacing.md },
  typeBtn: (active) => ({
    flex: "0 0 auto",
    border: `1px solid ${active ? PL : LINE}`,
    background: active ? "#F1E9FE" : PAPER,
    color: active ? "#6f27d6" : SUB,
    borderRadius: tokens.radius.md,
    padding: "10px 14px",
    minHeight: 44,
    font: `600 12.5px ${tokens.fontFamily}`,
    cursor: "pointer",
  }),
  preview: (bg) => ({
    height: 64,
    borderRadius: tokens.radius.md,
    border: `1px solid ${LINE}`,
    background: bg,
    marginBottom: tokens.spacing.md,
  }),
  stop: { display: "flex", alignItems: "center", gap: tokens.spacing.sm, marginBottom: tokens.spacing.sm },
  colorInput: { width: 44, height: 44, padding: 0, border: `1px solid ${LINE}`, borderRadius: 8, cursor: "pointer", background: PAPER },
  range: { flex: 1, minHeight: 24 },
  pct: { width: 40, textAlign: "right", fontSize: 12, color: SUB, fontVariantNumeric: "tabular-nums" },
  iconBtn: {
    width: 44,
    height: 44,
    border: `1px solid ${LINE}`,
    borderRadius: 8,
    background: PAPER,
    color: SUB,
    cursor: "pointer",
    fontSize: 18,
    lineHeight: 1,
  },
  addBtn: {
    border: `1px dashed ${PL}`,
    background: "#F7F2FF",
    color: "#6f27d6",
    borderRadius: 8,
    padding: "10px 14px",
    minHeight: 44,
    font: `600 12.5px ${tokens.fontFamily}`,
    cursor: "pointer",
    width: "100%",
  },
  label: { fontSize: 11, color: SUB, display: "block", marginBottom: 4, fontWeight: 600 },
  angleWrap: { marginTop: tokens.spacing.sm },
  disabled: { opacity: 0.4, cursor: "not-allowed" },
};

const GradientBuilderPanel = ({ gradient: gProp, onChange = () => {}, center = "50% 50%" }) => {
  const g = gProp && gProp.stops ? gProp : defaultGradientState();
  const type = GRADIENT_TYPES.includes(g.type) ? g.type : "linear";
  const stops = Array.isArray(g.stops) ? g.stops : defaultGradientState().stops;

  const previewBg = useMemo(() => {
    if (type === "conic") return buildSegmentConicCss({ ...g, type }, { center, loop: g.loop });
    return buildGradientCss({ ...g, type });
  }, [g, type, center]);

  const emit = (patch) => onChange({ ...g, type, stops, ...patch });

  const setType = (t) => emit({ type: t });

  const setStopColor = (i, color) => {
    const next = stops.map((s, idx) => (idx === i ? { ...s, color } : s));
    emit({ stops: next });
  };
  const setStopPos = (i, position) => {
    const next = stops.map((s, idx) => (idx === i ? { ...s, position: clampPosition(position) } : s));
    emit({ stops: next });
  };
  const addStop = () => {
    if (stops.length >= MAX_GRADIENT_STOPS) return;
    // Insert a mid-point stop between the last two, colour = last stop's colour.
    const last = stops[stops.length - 1] || { color: "#6D5EF6", position: 100 };
    const prev = stops[stops.length - 2] || { color: "#9B5AF8", position: 0 };
    const mid = clampPosition(Math.round((prev.position + last.position) / 2));
    const next = [...stops.slice(0, -1), { color: last.color, position: mid }, last];
    emit({ stops: next });
  };
  const removeStop = (i) => {
    if (stops.length <= MIN_GRADIENT_STOPS) return;
    emit({ stops: stops.filter((_, idx) => idx !== i) });
  };
  const setAngle = (angle) => emit({ angle: clampAngle(angle) });

  const showAngle = type === "linear" || type === "conic";
  const angleKey = type === "conic" ? "fromAngle" : "angle";
  const angleVal = type === "conic" ? clampAngle(g.fromAngle) : clampAngle(g.angle);

  return (
    <section style={S.panel} aria-label="Gradient builder" data-gradient-builder>
      <h3 style={S.h}>Gradient</h3>
      <p style={S.sub}>Blend two or more colours across the Segment.</p>

      {/* type selector */}
      <div role="radiogroup" aria-label="Gradient type" style={S.row}>
        {GRADIENT_TYPES.map((t) => {
          const active = type === t;
          return (
            <button
              key={t}
              role="radio"
              aria-checked={active}
              tabIndex={active ? 0 : -1}
              style={S.typeBtn(active)}
              onClick={() => setType(t)}
              data-grad-type={t}
            >
              {TYPE_LABEL[t]}
            </button>
          );
        })}
      </div>

      {/* live preview (canonical CSS) */}
      <div style={S.preview(previewBg)} role="img" aria-label={`Gradient preview: ${TYPE_LABEL[type]}`} data-grad-preview />

      {/* stops */}
      <div role="group" aria-label="Gradient colour stops">
        <span style={S.label}>Colour stops ({stops.length})</span>
        {stops.map((s, i) => (
          <div key={i} style={S.stop} data-grad-stop={i}>
            <input
              type="color"
              value={/^#[0-9A-Fa-f]{6}$/.test(s.color) ? s.color : "#6D5EF6"}
              onChange={(e) => setStopColor(i, e.target.value)}
              style={S.colorInput}
              aria-label={`Stop ${i + 1} colour`}
              data-grad-stop-color={i}
            />
            <input
              type="range"
              min={0}
              max={100}
              value={clampPosition(s.position)}
              onChange={(e) => setStopPos(i, Number(e.target.value))}
              style={S.range}
              aria-label={`Stop ${i + 1} position`}
              data-grad-stop-pos={i}
            />
            <span style={S.pct} data-grad-stop-pct={i}>{clampPosition(s.position)}%</span>
            <button
              type="button"
              onClick={() => removeStop(i)}
              disabled={stops.length <= MIN_GRADIENT_STOPS}
              style={{ ...S.iconBtn, ...(stops.length <= MIN_GRADIENT_STOPS ? S.disabled : {}) }}
              aria-label={`Remove stop ${i + 1}`}
              data-grad-stop-remove={i}
            >
              &times;
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addStop}
          disabled={stops.length >= MAX_GRADIENT_STOPS}
          style={{ ...S.addBtn, ...(stops.length >= MAX_GRADIENT_STOPS ? S.disabled : {}) }}
          aria-label="Add colour stop"
          data-grad-add-stop
        >
          + Add stop{stops.length >= MAX_GRADIENT_STOPS ? ` (max ${MAX_GRADIENT_STOPS})` : ""}
        </button>
      </div>

      {/* angle (linear + conic) */}
      {showAngle && (
        <div style={S.angleWrap}>
          <label style={S.label} htmlFor="grad-angle">
            {type === "conic" ? "Start angle" : "Angle"}: {angleVal}&deg;
          </label>
          <input
            id="grad-angle"
            type="range"
            min={0}
            max={360}
            value={angleVal}
            onChange={(e) =>
              type === "conic"
                ? emit({ fromAngle: clampAngle(Number(e.target.value)) })
                : setAngle(Number(e.target.value))
            }
            style={{ ...S.range, width: "100%" }}
            aria-label={type === "conic" ? "Conic start angle" : "Linear angle"}
            data-grad-angle
          />
        </div>
      )}

      {type === "conic" && (
        <p style={{ ...S.sub, marginTop: tokens.spacing.sm }} data-conic-note>
          Conic centres on the Segment and loops seamlessly - no hard seam at the wrap.
        </p>
      )}
    </section>
  );
};

export default GradientBuilderPanel;
